import { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';
const UPLOAD_ACCEPT = '.pdf,.jpg,.jpeg,.png';

const groupKey = (group) => group.map((option) => option.column).join('|');

// Documents the AI agent asked the customer for. Each group is one upload; a
// group with several options (water bill or gas bill) needs any one of them.
// Uploaded groups disappear, and once none are left the customer submits.
function RequiredDocumentsPanel({ onUploaded }) {
    const session = JSON.parse(localStorage.getItem('userSession') || '{}');
    const userId = session?.userId;
    const token = session?.token || '';

    const [state, setState] = useState(null); // { pending, readyToSubmit, documentsStatus }
    const [choice, setChoice] = useState({}); // group key -> chosen documentType
    const [busy, setBusy] = useState(''); // group key being uploaded, or 'submit'
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const inputRefs = useRef({});

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    const load = useCallback(async () => {
        if (!userId) return;
        try {
            const res = await fetch(`${API_URL}/api/documents/required`, { headers: authHeaders });
            const json = await res.json().catch(() => ({}));
            if (res.ok && json.success) setState(json);
        } catch {
            // The rest of the page still works without this panel.
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, userId]);

    useEffect(() => {
        load();
    }, [load]);

    const uploadFor = async (group, file) => {
        if (!file) return;
        const key = groupKey(group);
        const documentType = choice[key] || group[0].documentType;
        const form = new FormData();
        form.append('document', file);
        form.append('documentType', documentType);
        form.append('userId', userId);

        setBusy(key);
        setError('');
        setNotice('');
        try {
            const res = await fetch(`${API_URL}/api/documents/upload`, {
                method: 'POST',
                headers: authHeaders,
                body: form
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Upload failed. Please try again.');
            if (json.requiredDocuments) setState(json.requiredDocuments);
            else await load();
            onUploaded?.();
        } catch (err) {
            setError(err.message || 'Upload failed. Please try again.');
        } finally {
            setBusy('');
        }
    };

    const submit = async () => {
        setBusy('submit');
        setError('');
        try {
            const res = await fetch(`${API_URL}/api/documents/submit`, {
                method: 'POST',
                headers: authHeaders
            });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not submit the documents.');
            setState(json);
            setNotice(json.message || 'Documents submitted.');
        } catch (err) {
            setError(err.message || 'Could not submit the documents.');
        } finally {
            setBusy('');
        }
    };

    const pending = state?.pending || [];
    if (!state || (!pending.length && !state.readyToSubmit && !notice)) return null;

    const done = !pending.length;

    return (
        <section className={`dc-req${done ? ' is-done' : ''}`}>
            <div className="dc-req-head">
                <span className="dc-req-head-icon" aria-hidden="true">{done ? '✅' : '📋'}</span>
                <div className="dc-req-head-copy">
                    <span className={`dc-badge ${done ? 'tone-green' : 'tone-amber'}`}>
                        <i aria-hidden="true" />{done ? 'All set' : 'Action needed'}
                    </span>
                    <h2 className="dc-req-title">Documents requested</h2>
                    <p className="dc-req-sub">
                        {pending.length
                            ? `We need ${pending.length} more document${pending.length === 1 ? '' : 's'} to continue with your application.`
                            : state.readyToSubmit
                                ? 'All requested documents are uploaded. Submit them for review.'
                                : notice}
                    </p>
                </div>
                {pending.length > 0 && (
                    <span className="dc-req-count" aria-hidden="true">
                        <strong>{pending.length}</strong>
                        <small>pending</small>
                    </span>
                )}
            </div>

            {error && (
                <div className="pg-alert pg-alert-danger"><div className="pg-alert-body"><p>{error}</p></div></div>
            )}

            {pending.length > 0 && (
                <div className="dc-req-list">
                    {pending.map((group) => {
                        const key = groupKey(group);
                        const selected = choice[key] || group[0].documentType;
                        const uploading = busy === key;
                        const title = group.map((option) => option.label).join(' or ');
                        // The reviewer's note for the chosen option, else any note in the group.
                        const reason = (group.find((option) => option.documentType === selected)?.reason
                            || group.find((option) => option.reason)?.reason || '').trim();
                        return (
                            <div key={key} className={`dc-req-item${uploading ? ' is-uploading' : ''}`}>
                                <span className="dc-req-item-icon" aria-hidden="true">📄</span>
                                <div className="dc-req-item-body">
                                    <div className="dc-req-item-title">{title}</div>
                                    {reason && (
                                        <div className="dc-req-item-reason">
                                            <strong>Why: </strong>{reason}
                                        </div>
                                    )}
                                    {group.length > 1 ? (
                                        <div className="dc-req-choice" role="radiogroup" aria-label="Which one will you upload?">
                                            <span className="dc-req-choice-label">Upload any one:</span>
                                            {group.map((option) => (
                                                <label
                                                    key={option.column}
                                                    className={`dc-req-chip${selected === option.documentType ? ' is-active' : ''}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`req-${key}`}
                                                        value={option.documentType}
                                                        checked={selected === option.documentType}
                                                        disabled={!!busy}
                                                        onChange={() => setChoice((prev) => ({ ...prev, [key]: option.documentType }))}
                                                    />
                                                    {option.label}
                                                </label>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="dc-req-item-sub">PDF, JPG or PNG</div>
                                    )}
                                </div>
                                <input
                                    type="file"
                                    accept={UPLOAD_ACCEPT}
                                    aria-label={`Upload ${title}`}
                                    ref={(el) => { inputRefs.current[key] = el; }}
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0] || null;
                                        e.target.value = '';
                                        uploadFor(group, file);
                                    }}
                                />
                                <button
                                    type="button"
                                    className="dc-req-btn"
                                    disabled={!!busy}
                                    onClick={() => inputRefs.current[key]?.click()}
                                >
                                    {uploading ? 'Uploading…' : <><span aria-hidden="true">↑</span> Upload</>}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            {!pending.length && state.readyToSubmit && (
                <div className="dc-req-actions">
                    <button
                        type="button"
                        className="dc-req-btn"
                        disabled={busy === 'submit'}
                        onClick={submit}
                    >
                        {busy === 'submit' ? 'Submitting…' : 'Submit documents'}
                    </button>
                </div>
            )}
        </section>
    );
}

export default RequiredDocumentsPanel;
